import React from "react"
import { useTranslation } from "react-i18next"

import { useUnit } from "@/components/unit/use-unit"
import { useUserSettings } from "@/contexts"
import { useFormatDecimal } from "@/formatters"
import type { Department } from "@/models/department"
import { UnitName } from "@/models/unit"
import {
  DataGridColumnHeader,
  DataGridNumberEditCell,
  GridActionTooltipButton,
} from "@/nzds/data-grid"
import { useFlags } from "@/services/feature"
import {
  squareFeetToSquareMeters,
  squareMetersToSquareFeet,
} from "@/utils/calculations"
import { EMPTY_DATA_PLACEHOLDER } from "@/utils/constants"
import { getDisplayValue } from "@/utils/value"

import CancelIcon from "@mui/icons-material/Close"
import EditIcon from "@mui/icons-material/Edit"
import SaveIcon from "@mui/icons-material/Save"
import { GridActionsCellItem, GridRowModes } from "@mui/x-data-grid-pro"
import type {
  GridColDef,
  GridRenderEditCellParams,
  GridRowId,
  GridRowModesModel,
} from "@mui/x-data-grid-pro"

import { baseSiteColumnDef, baseSiteNumberColumnDef } from "../../../models"
import type { SiteExplorerGridRecord } from "../../../services/use-get-site-explorer"

type CommonSiteExplorerGridColumnName =
  | "actions"
  | "buildingArea"
  | "buildingType"
  | "combinedEmissions"
  | "combinedEnergyUsage"
  | "combinedEui"
  | "electricityEmissions"
  | "electricityEui"
  | "electricityUsage"
  | "group"
  | "naturalGasEmissions"
  | "naturalGasEui"
  | "naturalGasUsage"

interface UseCommonSiteExplorerGridColumnsProps {
  buildingTypes: string[] | undefined
  departments: Department[] | undefined
  handleRowEditCancelClick: (id: GridRowId) => void
  handleRowEditClick: (id: GridRowId) => void
  handleRowEditSaveClick: (id: GridRowId) => void
  rowModesModel: GridRowModesModel
}

export const useCommonSiteExplorerGridColumns = ({
  buildingTypes,
  departments,
  handleRowEditCancelClick,
  handleRowEditClick,
  handleRowEditSaveClick,
  rowModesModel,
}: UseCommonSiteExplorerGridColumnsProps): Record<
  CommonSiteExplorerGridColumnName,
  GridColDef<SiteExplorerGridRecord>
> => {
  const { t } = useTranslation()
  const { unitsSystem } = useUserSettings()

  const isAreaMetric = unitsSystem === "ja-JP" || unitsSystem === "th-TH"

  const { formatDecimal: formatDecimalToHundredths } = useFormatDecimal({
    precision: 2,
  })

  const { formatDecimal: formatDecimalToInteger } = useFormatDecimal({
    precision: 0,
  })

  const { formatDecimal: formatEUI } = useFormatDecimal(
    {
      precision: 2,
    },
    {
      showUnit: false,
      unit: UnitName.KiloBritishThermalUnitPerSquareFoot,
    }
  )

  const { formatDecimal: formatUsage } = useFormatDecimal(
    {
      precision: 0,
    },
    {
      showUnit: false,
      unit: UnitName.KiloBritishThermalUnit,
    }
  )

  const kbtuUnit = useUnit(UnitName.KiloBritishThermalUnit)
  const kbtuPerSqFtUnit = useUnit(
    UnitName.KiloBritishThermalUnitPerSquareFoot,
    null
  )
  const mtCo2eUnit = useUnit(UnitName.MetricTonsOfCarbonDioxideEquivalent)
  const sqFtUnit = useUnit(UnitName.SquareFoot)

  const flags = useFlags()

  const departmentNames: string[] = React.useMemo(() => {
    return departments?.map((department) => department.name) ?? []
  }, [departments])

  const columns = React.useMemo(() => {
    const columnDefMap: Record<
      CommonSiteExplorerGridColumnName,
      GridColDef<SiteExplorerGridRecord>
    > = {
      actions: {
        ...baseSiteNumberColumnDef,
        align: "left",
        cellClassName: "actions",
        field: "actions",
        getActions: ({ id }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit

          if (isInEditMode) {
            return [
              <GridActionsCellItem
                component={GridActionTooltipButton}
                data-testid="save"
                icon={<SaveIcon color="primary" />}
                key="save-action"
                label={t("button.save")}
                onClick={() => {
                  handleRowEditSaveClick(id)
                }}
              />,
              <GridActionsCellItem
                className="textPrimary"
                component={GridActionTooltipButton}
                icon={<CancelIcon />}
                key="cancel-action"
                label={t("button.cancel")}
                onClick={() => {
                  handleRowEditCancelClick(id)
                }}
              />,
            ]
          }

          return [
            <GridActionsCellItem
              className="textPrimary"
              component={GridActionTooltipButton}
              icon={<EditIcon color="primary" />}
              key="edit-action"
              label={t("button.edit")}
              onClick={() => {
                handleRowEditClick(id)
              }}
            />,
          ]
        },
        headerAlign: "left",
        headerName: t("gridColumn.actions.name"),
        type: "actions",
        width: 104,
      },
      buildingArea: {
        ...baseSiteNumberColumnDef,
        editable: true,
        field: "sq_ft",
        headerName: t("fields.buildingArea.title"),
        renderEditCell: (
          params: GridRenderEditCellParams<
            SiteExplorerGridRecord,
            SiteExplorerGridRecord["sq_ft"]
          >
        ) => (
          <DataGridNumberEditCell<SiteExplorerGridRecord>
            {...params}
            allowNegative={false}
          />
        ),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("fields.buildingArea.title")}
            unit={sqFtUnit}
          />
        ),
        valueFormatter: (value: SiteExplorerGridRecord["sq_ft"]): string =>
          formatDecimalToInteger(value),
        valueGetter: (
          value: SiteExplorerGridRecord["sq_ft"]
        ): number | null => {
          const sqFtValue = Number.parseFloat(value ?? "")

          if (!Number.isFinite(sqFtValue)) {
            return null
          }

          return Math.round(
            isAreaMetric ? squareFeetToSquareMeters(sqFtValue) : sqFtValue
          )
        },
        valueSetter: (
          value: number | null,
          row: SiteExplorerGridRecord
        ): SiteExplorerGridRecord => {
          if (value == null) {
            return { ...row, sq_ft: null }
          }

          const sqFtValue = Math.round(
            isAreaMetric ? squareMetersToSquareFeet(value) : value
          )

          return { ...row, sq_ft: String(sqFtValue) }
        },
        width: 140,
      },
      buildingType: {
        ...baseSiteColumnDef,
        editable: true,
        field: "building_type",
        headerName: t("fields.buildingType.title"),
        type: "singleSelect",
        valueFormatter: (value: SiteExplorerGridRecord["building_type"]) => {
          if (!value) {
            return EMPTY_DATA_PLACEHOLDER
          }

          return t(value, { ns: "database" })
        },
        // Convert null to empty string to avoid error in the singleSelect editor
        valueGetter: (value: SiteExplorerGridRecord["building_type"]) =>
          value ?? "",
        getOptionLabel: (value) => {
          return t(value, { ns: "database" })
        },
        valueOptions: buildingTypes,
        width: 160,
      },
      combinedEmissions: {
        ...baseSiteNumberColumnDef,
        field: "total_emissions_mt_co2e",
        headerName: t("gridColumn.combinedEmissions.name"),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.combinedEmissions.name")}
            tooltipContent={t("gridColumn.combinedEmissions.description")}
            unit={mtCo2eUnit}
          />
        ),
        valueFormatter: (
          value: SiteExplorerGridRecord["total_emissions_mt_co2e"]
        ) => formatDecimalToHundredths(value),
        width: 216,
      },
      combinedEnergyUsage: {
        ...baseSiteNumberColumnDef,
        field: "total_energy_consumption_kbtu",
        headerName: t("gridColumn.combinedEnergyUsage.name"),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.combinedEnergyUsage.name")}
            tooltipContent={t("gridColumn.combinedEnergyUsage.description")}
            unit={kbtuUnit}
          />
        ),
        valueFormatter: (
          value: SiteExplorerGridRecord["total_energy_consumption_kbtu"]
        ) => formatUsage(value),
        width: 236,
      },
      combinedEui: {
        ...baseSiteNumberColumnDef,
        field: "total_kbtu_per_sq_ft",
        headerName: t("gridColumn.combinedEui.name"),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.combinedEui.name")}
            tooltipContent={t("gridColumn.combinedEui.description")}
            unit={kbtuPerSqFtUnit}
          />
        ),
        valueFormatter: (
          value: SiteExplorerGridRecord["total_mmbtu_per_sq_ft"]
        ) => formatEUI(value),
        width: 160,
      },
      electricityEmissions: {
        ...baseSiteNumberColumnDef,
        field: "electricity_mt_co2e",
        headerName: t("gridColumn.electricityEmissions.name"),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.electricityEmissions.name")}
            unit={mtCo2eUnit}
          />
        ),
        valueFormatter: (
          value: SiteExplorerGridRecord["electricity_mt_co2e"]
        ) => formatDecimalToHundredths(value),
        width: 180,
      },
      electricityEui: {
        ...baseSiteNumberColumnDef,
        field: "electricity_kbtu_per_sq_ft",
        headerName: t("gridColumn.electricityEui.name"),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.electricityEui.name")}
            unit={kbtuPerSqFtUnit}
          />
        ),
        valueFormatter: (
          value: SiteExplorerGridRecord["electricity_kbtu_per_sq_ft"]
        ) => formatEUI(value),
        width: 140,
      },
      electricityUsage: {
        ...baseSiteNumberColumnDef,
        field: "electricity_kbtu",
        headerName: t("gridColumn.electricityUsage.name"),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.electricityUsage.name")}
            unit={kbtuUnit}
          />
        ),
        valueFormatter: (value: SiteExplorerGridRecord["electricity_kbtu"]) =>
          formatUsage(value),
        width: 152,
      },
      group: {
        ...baseSiteColumnDef,
        editable: !flags.use_flexible_hierarchy_for_site_ownerships,
        field: flags.use_flexible_hierarchy_for_site_ownerships
          ? "organizational_unit_name"
          : "department_name",
        headerName: t("fields.group.title"),
        type: "singleSelect",
        valueFormatter: (value: SiteExplorerGridRecord["department_name"]) =>
          getDisplayValue(value),
        // Convert null to empty string to avoid error in the singleSelect editor
        valueGetter: (value: SiteExplorerGridRecord["department_name"]) =>
          value ?? "",
        valueOptions: departmentNames,
        width: 160,
      },
      naturalGasEmissions: {
        ...baseSiteNumberColumnDef,
        field: "nat_gas_mt_co2e",
        headerName: t("gridColumn.naturalGasEmissions.name"),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.naturalGasEmissions.name")}
            unit={mtCo2eUnit}
          />
        ),
        valueFormatter: (value: SiteExplorerGridRecord["nat_gas_mt_co2e"]) =>
          formatDecimalToHundredths(value),
        width: 180,
      },
      naturalGasEui: {
        ...baseSiteNumberColumnDef,
        field: "nat_gas_kbtu_per_sq_ft",
        headerName: t("gridColumn.naturalGasEui.name"),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.naturalGasEui.name")}
            unit={kbtuPerSqFtUnit}
          />
        ),
        valueFormatter: (
          value: SiteExplorerGridRecord["nat_gas_kbtu_per_sq_ft"]
        ) => formatEUI(value),
        width: 140,
      },
      naturalGasUsage: {
        ...baseSiteNumberColumnDef,
        field: "nat_gas_kbtu",
        headerName: t("gridColumn.naturalGasUsage.name"),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.naturalGasUsage.name")}
            unit={kbtuUnit}
          />
        ),
        valueFormatter: (value: SiteExplorerGridRecord["nat_gas_kbtu"]) =>
          formatUsage(value),
        width: 180,
      },
    }

    return columnDefMap
  }, [
    buildingTypes,
    departmentNames,
    flags.use_flexible_hierarchy_for_site_ownerships,
    formatDecimalToInteger,
    formatDecimalToHundredths,
    formatEUI,
    formatUsage,
    handleRowEditSaveClick,
    handleRowEditCancelClick,
    handleRowEditClick,
    isAreaMetric,
    kbtuUnit,
    kbtuPerSqFtUnit,
    mtCo2eUnit,
    rowModesModel,
    sqFtUnit,
    t,
  ])

  return columns
}
